import { useDebounceFn } from "@reactuses/core";
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import type { RouterHistory } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import z from "zod";

// 1-based so a shared link reads the way the paginator does. every list param
// pairs .default, which keeps it optional for links and redirects, with .catch,
// which keeps a hand-edited url from failing the route
export const pageSearchParam = z.coerce
  .number()
  .int()
  .min(1)
  .default(1)
  .catch(1);

const DEFAULT_DEBOUNCE_WAIT_MS = 300;

type HistoryActionType = Parameters<
  Parameters<RouterHistory["subscribe"]>[0]
>[0]["action"]["type"];

/**
 * List state (filters and page) that lives in the url. The schema's defaults
 * are what an empty url means, so a value equal to its default is dropped from
 * the url rather than written, and changing a filter goes back to page 1.
 */
export function useListSearch<T extends { page: number }>(
  schema: z.ZodType<T>,
) {
  const raw = useSearch({ strict: false });
  const search = useMemo(() => schema.parse(raw), [raw, schema]);
  const defaults = useMemo(() => schema.parse({}), [schema]);
  const navigate = useNavigate();

  // only the changed keys are written, on top of the router's latest location,
  // so two updates made before a rerender both survive
  const write = useCallback(
    (updates: Partial<T>) => {
      const params = Object.fromEntries(
        Object.entries(updates).map(([key, value]) => [
          key,
          sameValue(value, defaults[key as keyof T]) ? undefined : value,
        ]),
      );
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, ...params }),
        replace: true,
      });
    },
    [defaults, navigate],
  );

  const setFilters = useCallback(
    (updates: Partial<Omit<T, "page">>) => {
      write({ ...updates, page: defaults.page } as Partial<T>);
    },
    [defaults.page, write],
  );

  const setPageIndex = useCallback(
    (pageIndex: number) => {
      write({ page: pageIndex + 1 } as Partial<T>);
    },
    [write],
  );

  return {
    search,
    setFilters,
    pageIndex: search.page - 1,
    setPageIndex,
  };
}

/**
 * Text input bound to a url value: keystrokes stay local and reach the url
 * once typing pauses.
 */
export function useDebouncedInput(
  value: string,
  onCommit: (value: string) => void,
  wait = DEFAULT_DEBOUNCE_WAIT_MS,
) {
  const [draft, setDraft] = useState(value);
  const [committed, setCommitted] = useState(value);
  // null marks a history move: the next render resyncs even to an equal value
  const [seen, setSeen] = useState<string | null>(value);
  const [resyncs, setResyncs] = useState(0);

  // useSearch only shows a back/forward once the destination route has run its
  // beforeLoad and loaders, and a debounce timer can fire in that gap. the
  // history notifies the moment the entry changes, so moves are counted there.
  // a push keeps the pending commit: it lands on the new entry, typing intact
  const router = useRouter();
  const historyMoves = useRef(0);
  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        if (!isHistoryMove(action.type)) return;
        historyMoves.current += 1;
        setSeen(null);
      }),
    [router],
  );

  // a keystroke carries the counts it was typed at, so a commit still pending
  // from before a history move or an outside url change is dropped instead of
  // undoing it. both counts only grow, so a round trip cannot match them again
  const { run } = useDebounceFn(
    (next: string, typedAtResync: number, typedAtMove: number) => {
      if (typedAtResync !== resyncs || typedAtMove !== historyMoves.current) {
        return;
      }
      setCommitted(next);
      onCommit(next);
    },
    wait,
  );

  // back/forward moves the url under the input and has to show up in it, while
  // the url catching up to our own debounced write must not reset later typing.
  // a move also drops the pending commit, so the draft it would have written
  // goes too, even when the entry moved to holds the same value
  if (value !== seen) {
    const moved = seen === null;
    setSeen(value);
    if (moved || value !== committed) {
      setDraft(value);
      setCommitted(value);
      setResyncs((count) => count + 1);
    }
  }

  const setValue = useCallback(
    (next: string) => {
      setDraft(next);
      run(next, resyncs, historyMoves.current);
    },
    [resyncs, run],
  );

  return [draft, setValue] as const;
}

function sameValue(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function isHistoryMove(type: HistoryActionType) {
  return type === "BACK" || type === "FORWARD" || type === "GO";
}
